import React, { useEffect, useCallback, useState } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    List,
    ListItem
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import dayjs from "dayjs";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';
import DatePickerComponent from "../../common/DatePickerComponent";

import { useTransactions, targetTypes, actionTypes, updateStatusTypes, payloadTypes } from '../../../hooks/api/useTransactions';
import { useWallets, targetTypes as walletTargetTypes } from '../../../hooks/api/useWallets';

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';

import {
    validationStatusTypeSchema
} from "../../../schema/transaction/transactionSchema";

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';

import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateWalletRequests({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@transaction"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, updateStatus } = useTransactions({ targetType: targetTypes.request });
    const { list: listWalletUsers } = useWallets({ targetType: walletTargetTypes.walletUser });

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.wallet.id, scopeTypes.wallet.wallet.id);

    const { setSystemLoading } = useAppContextFeatures();

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState(null)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [walletUserData, setWalletUserData] = useState([])

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { transaction } = data;

            let transactionName = ""

            if (transaction.action === payloadTypes.request.transfer.action) {
                transactionName = actionTypes.request.transfer.id
            }
            if (transaction.action === payloadTypes.request.topup.action) {
                transactionName = actionTypes.request.topup.id
            }
            if (transaction.action === payloadTypes.request.withdraw.action) {
                transactionName = actionTypes.request.withdraw.id
            }

            setSavedValues({
                actionType: transactionName.toUpperCase(),
                transactionAmount: parseFloat(transaction.transaction_amount).toFixed(2),
                referenceNo: transaction.reference_no ? transaction.reference_no : "",
                description: transaction.description,
                remark: transaction.remark ? transaction.remark : "",
                fromWalletUserId: transaction.source_refer_id,
                toWalletUserId: transaction.destination_refer_id,
                statusType: transaction.transaction_status ? transaction.transaction_status : "",
                transactionDate: dayjs(transaction.transaction_date),
                internalReferenceNo: transaction.internal_reference_no ? transaction.internal_reference_no : "",
                internalRemark: transaction.internal_remark ? transaction.internal_remark : "",
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const fetchWalletUsers = async () => {

        setSystemLoading(true)

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "id",
                    dir: 0
                }
            }

            const { data } = await listWalletUsers(props)

            setWalletUserData(data?.wallet_users)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)
    }

    useEffect(() => {
        loadData(id);
        fetchWalletUsers()
    }, [])

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                transaction: {
                    status: currentValues?.statusType,
                    internal_reference_no: currentValues?.internalReferenceNo,
                    internal_remark: currentValues?.internalRemark,
                }
            }
            const response = await updateStatus({ id, data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("transactions/walletRequests"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const formik = useFormik({
        initialValues: savedValues,
        // validationSchema: (savedValues?.statusType !== updateStatusTypes.pending)? validationStatusTypeSchema : "",
        validationSchema: validationStatusTypeSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderForm = () => {

        if (!values) return <></>

        let fromUserInfo = []
        let toUserInfo = []
        let fromFirstname = ""
        let fromLastname = ""
        let fromWalletName = ""
        let toFirstname = ""
        let toLastname = ""
        let toWalletName = ""
        let currency = ""

        fromUserInfo = walletUserData.filter(i => i.id === values?.fromWalletUserId)

        if (fromUserInfo.length) {
            fromFirstname = fromUserInfo[0].firstname
            fromLastname = fromUserInfo[0].lastname
            fromWalletName = fromUserInfo[0].wallet_name
            currency = fromUserInfo[0].wallet_currency
        }

        toUserInfo = walletUserData.filter(i => i.id === values?.toWalletUserId)

        if (toUserInfo.length) {
            toFirstname = toUserInfo[0].firstname
            toLastname = toUserInfo[0].lastname
            toWalletName = toUserInfo[0].wallet_name
        }

        let _props = {}

        if (savedValues?.statusType === updateStatusTypes.pending) {
            _props = {}
        }
        else {
            _props = {
                disabled: true
            }
        }

        // let availableStatus = Object.keys(updateStatusTypes).filter(i => i !== updateStatusTypes.pending)
        let availableStatus = Object.keys(updateStatusTypes).map(i => i)

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>

                        <Grid item xs={12} sm={12} md={2.5} textAlign={"left"}>
                            <CommonSectionHeader
                                title={t('walletRequestConfirmation')}
                                subTitle={t('detailsOfWalletRequest')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={9.5}>
                            <Grid container spacing={1}>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"actionType"}
                                        label={t("actionType")}
                                        type="text"
                                        variant="outlined"
                                        value={values.actionType}
                                    />
                                </Grid>

                                <Grid item xs={0} sm={0} md={6}><></></Grid>

                                <Grid item xs={3} sm={3} md={3}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"fromUsername"}
                                        label={t("fromCustomer")}
                                        type="text"
                                        variant="outlined"
                                        value={`${fromFirstname} ${fromLastname}`}
                                    />
                                </Grid>

                                <Grid item xs={3} sm={3} md={3}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"fromWalletUserId"}
                                        label={t("availableWallet")}
                                        type="text"
                                        variant="outlined"
                                        value={`${fromWalletName} (${currency})`}
                                    />
                                </Grid>

                                {
                                    (values?.actionType === actionTypes.request.transfer.id) ?
                                        (
                                            <>
                                                <Grid item xs={12} sm={12} md={3}>
                                                    <TextField
                                                        disabled
                                                        margin="dense"
                                                        size="small"
                                                        fullWidth
                                                        name={"toUsername"}
                                                        label={t("toCustomer")}
                                                        type="text"
                                                        variant="outlined"
                                                        value={`${toFirstname} ${toLastname}`}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={12} md={3}>
                                                    <TextField
                                                        disabled
                                                        margin="dense"
                                                        size="small"
                                                        fullWidth
                                                        name={"toWalletUserId"}
                                                        label={t("availableWallet")}
                                                        type="text"
                                                        variant="outlined"
                                                        value={`${toWalletName} (${currency})`}
                                                    />
                                                </Grid>
                                            </>
                                        )
                                        :
                                        (
                                            <Grid item xs={6} sm={6} md={6}>
                                                <></>
                                            </Grid>
                                        )
                                }

                                <Grid item xs={6} sm={6} md={3}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"transactionAmount"}
                                        label={t("transactionAmount")}
                                        type="text"
                                        variant="outlined"
                                        value={values.transactionAmount}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <DatePickerComponent
                                        config={{
                                            name: "transactionDate",
                                            label: t("transactionDate")
                                        }}
                                        formik={{
                                            values,
                                            errors,
                                            touched,
                                            handleChange,
                                            setFieldValue
                                        }}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"referenceNo"}
                                        label={t("referenceNo")}
                                        type="text"
                                        variant="outlined"
                                        value={values.referenceNo ? values.referenceNo : ""}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t("description")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.description}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        disabled
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"remark"}
                                        label={t("remark")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.remark}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12}>
                                    <Divider variant="horizontal"
                                        sx={{
                                            mt: 1,
                                            mb: 1
                                        }}
                                    >
                                        {t("statusConfirmationInfo")}
                                    </Divider>
                                </Grid>

                                {
                                    (savedValues?.statusType === updateStatusTypes.pending) ?
                                        (
                                            <>
                                                <Grid item xs={12} sm={6} md={6}>
                                                    <FormControl
                                                        margin="dense"
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(errors.statusType)}
                                                    >
                                                        <InputLabel id="statusType">{t("statusType")}</InputLabel>
                                                        <Select
                                                            labelId="statusType"
                                                            label={t("statusType")}
                                                            name={"statusType"}
                                                            margin="dense"
                                                            onChange={handleChange}
                                                            value={values.statusType}
                                                            sx={{
                                                                textAlign: 'left'
                                                            }}
                                                        >
                                                            {
                                                                availableStatus?.map((i, index) => {
                                                                    return (
                                                                        <MenuItem key={index} value={i}>{(i.toUpperCase())}</MenuItem>
                                                                    )
                                                                })
                                                            }
                                                        </Select>
                                                        <FormHelperText>
                                                            {errors.statusType}
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            </>
                                        )
                                        :
                                        (
                                            values?.statusType !== updateStatusTypes.pending &&

                                            <>
                                                <Grid item xs={6} sm={6} md={6}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 16,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {`Status: ${(values?.statusType).toUpperCase()}`}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} sm={6} md={6}>
                                                    <></>
                                                </Grid>
                                            </>
                                        )
                                }

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        {..._props}
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"internalReferenceNo"}
                                        label={t("internalReferenceNo")}
                                        type="text"
                                        variant="outlined"
                                        value={values.internalReferenceNo}
                                        onChange={handleChange}
                                        error={Boolean(errors?.internalReferenceNo)}
                                        helperText={errors?.internalReferenceNo}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        {..._props}
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"internalRemark"}
                                        label={t("internalRemark")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.internalRemark}
                                        onChange={handleChange}
                                        error={Boolean(errors?.internalRemark)}
                                        helperText={errors?.internalRemark}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                {
                    (savedValues?.statusType === updateStatusTypes.pending) ?
                        (
                            <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                                <Grid item md={12} textAlign={"right"}>
                                    <LoadingButton
                                        type="submit"
                                        // fullWidth
                                        variant="contained"
                                        sx={{
                                            mt: 3,
                                            mb: 2
                                        }}
                                        loading={isLoading}
                                    >
                                        {t('saveChanges')}
                                    </LoadingButton>
                                </Grid>
                            </Grid>
                        )
                        :
                        (
                            values?.statusType !== updateStatusTypes.pending &&

                            <></>
                        )
                }

            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateWalletRequest })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('updateWalletRequest')}
                breadcrumbs={[
                    {
                        displayText: t('walletRequests'),
                        props: {
                            href: getRoute("transactions/walletRequests")
                        }
                    },
                    {
                        displayText: t('updateWalletRequest'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}